import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
} from '@angular/core';
import { Router } from '@angular/router';
import {
    TelXLButtonComponent,
    ContactIconComponent,
    TelXLLoadingComponent,
} from '@dxp/elements';
import { IConversationCustomer, IWorkItem } from '@dxp/core/models';
import { HeartbeatService, MonitoringService } from '@dxp/core/services';
import { FormatDurationPipe } from '@dxp/core/pipes';
import { Duration } from 'date-fns';
import { secondsToDuration } from '@dxp/core/helpers';
import { WORKITEMS_STORE } from '@dxp/core/stores';
import {
    ContactIconUserTypes,
    ContactIconSizes,
    UnknownCustomer,
} from '@dxp/core/types';

@Component({
    selector: 'voice-monitor-call',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TelXLButtonComponent,
        ContactIconComponent,
        TelXLLoadingComponent,
        FormatDurationPipe,
    ],
    templateUrl: './voice-monitor-call.component.html',
    styleUrl: './voice-monitor-call.component.scss',
})
export class VoiceMonitorCallComponent {
    readonly #workItemStore = inject(WORKITEMS_STORE);
    readonly #router = inject(Router);
    readonly #monitoringService = inject(MonitoringService);
    readonly #heartbeatService = inject(HeartbeatService);

    readonly #now = this.#heartbeatService.lastHeartbeat;

    conversationId = input.required<string>();
    callId = input.required<number>();
    taskActive = input.required<boolean>();
    callEndTime = input.required<{ startedAt: Date; endedAt: Date }>();

    contactUserOptions = ContactIconUserTypes;
    contactUserSizes = ContactIconSizes;

    activeCallConversationTime = computed<Duration>(() => {
        const workItem = this.currentWorkItem();
        const activeCallTime = workItem?.conversations[0]?.queuedAt;

        if (workItem && activeCallTime) {
            const now = this.#now();

            const timer = Math.round(
                (now.getTime() - new Date(activeCallTime).getTime()) / 1000,
            );
            return secondsToDuration(timer);
        }

        return {};
    });

    currentWorkItem = computed<IWorkItem | undefined>(() => {
        const activeWorkItems = this.#workItemStore.workItems();
        return activeWorkItems.find(
            workItem =>
                workItem.primaryConversationId === this.conversationId() &&
                workItem.channelIdentifier === 'Monitor',
        );
    });

    /**
     * Active details for Monitoring Voice Conversation
     */
    details = computed(() => {
        const currentWorkItem = this.currentWorkItem();
        return {
            areReady: !!currentWorkItem,
            fullName:
                this.toName(
                    currentWorkItem?.primaryConversationCustomer,
                    currentWorkItem?.conversations[0]?.fromNumber,
                ) ?? '',
            callTime: this.activeCallConversationTime(),
            number: currentWorkItem?.conversations[0]?.fromNumber ?? '',
            numberDialled:
                currentWorkItem?.conversations[0]?.dialedNumber ?? '',
            queueName: currentWorkItem?.conversations[0]?.queueName ?? '',
            serviceName: currentWorkItem?.conversations[0]?.serviceName ?? '',
            callId: currentWorkItem?.conversations[0]?.voiceCallId ?? '',
        };
    });

    monitoredCallDuration = computed(() => {
        const timer = Math.round(
            (this.callEndTime().endedAt.getTime() -
                this.callEndTime().startedAt.getTime()) /
                1000,
        );
        return secondsToDuration(timer);
    });

    toName(
        customer: IConversationCustomer | undefined,
        incomingNumber: string | undefined,
    ): string {
        if (customer) {
            const customerName =
                customer.firstName || customer.lastName || customer.middleName;

            if (
                customer.customerIdentifier === incomingNumber &&
                !customerName
            ) {
                return UnknownCustomer;
            }

            const name = customerName
                ? `${customer.firstName} ${customer.middleName} ${customer.lastName}`.trim()
                : customer.customerIdentifier;
            return name ? name : UnknownCustomer;
        } else {
            return UnknownCustomer;
        }
    }

    stopCallMonitoring() {
        if (this.taskActive()) {
            this.#monitoringService
                .stopMonitoring(this.callId(), this.conversationId())
                .subscribe(() => {
                    this.#router.navigate(['/dashboard']);
                });
        } else {
            this.#router.navigate(['/dashboard']);
        }
    }
}
